import React, { createContext, useContext, useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { useNavigate } from 'react-router-dom';
import { cn } from "@/lib/utils";
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { useRef } from 'react';

// Context
const StepperContext = createContext();

// Main Stepper component (formerly StepperGroup)
export const Stepper = ({ steps, currentStep, setCurrentStep, children }) => {
    const [direction, setDirection] = useState('forward');

    const handleNextStep = () => {
        setDirection('forward');
        setCurrentStep((prevStep) => Math.min(prevStep + 1, steps.length - 1));
    };

    const handlePreviousStep = () => {
        setDirection('backward');
        setCurrentStep((prevStep) => Math.max(prevStep - 1, 0));
    };

    const goToStep = (stepIndex) => {
        const canGoToStep = stepIndex <= currentStep || steps.slice(0, stepIndex).every(step => step.isComplete);
        if (canGoToStep) {
            setDirection(stepIndex > currentStep ? 'forward' : 'backward');
            setCurrentStep(stepIndex);
        }
    };

    return (
        <StepperContext.Provider value={{ 
            steps, 
            currentStep, 
            handleNextStep, 
            handlePreviousStep,
            goToStep,
            direction
        }}>
            {children}
        </StepperContext.Provider>
    );
};

// Hook
export const useStepper = () => useContext(StepperContext);

// StepperButtons component
export const StepperButtons = ({ onSubmit, loading, submitButtonText = 'Save & Activate Monitor' }) => {
    const { currentStep, handleNextStep, handlePreviousStep, steps } = useStepper();
    const navigate = useNavigate();

    const handleClick = () => {
        if (currentStep >= steps.length - 1) {
            onSubmit();
        } else {
            handleNextStep();
        }
    };

    return (
        <div className="flex flex-col-reverse md:flex-row w-full max-w-2xl mx-auto md:space-x-4">
            {currentStep > 0 ? (
                <Button variant="outline" className="w-full mt-4 md:mt-0 md:w-1/3" onClick={handlePreviousStep}>Previous</Button>
            ) : (
                <Button variant="outline" className="w-full mt-4 md:mt-0 md:w-1/3" onClick={() => navigate('/monitors')}>Cancel</Button>
            )}
            <Button  
                className="w-full md:w-2/3" 
                onClick={handleClick}
                disabled={loading || !steps[currentStep].isComplete}
            >
                {currentStep >= steps.length - 1 ? submitButtonText : 'Continue'}
            </Button>
        </div>
    );
};

// StepperContent component
export const StepperContent = ({ children }) => {
    const { currentStep, direction } = useStepper();
    const childrenArray = React.Children.toArray(children);

    const variants = {
        enter: (direction) => ({
            x: direction === 'forward' ? '100%' : '-100%',
            opacity: 0,
            position: 'absolute',
        }),
        center: {
            x: 0,
            opacity: 1,
            position: 'relative',
        },
        exit: (direction) => ({
            x: direction === 'forward' ? '-100%' : '100%',
            opacity: 0,
            position: 'absolute',
        }),
    };
    
    const transition = {
        x: { type: 'tween', ease: 'easeInOut', duration: 0.3 },
        opacity: { duration: 0.2 },
    };    

    return (
        <AnimatePresence mode="sync" initial={false} custom={direction}>
            <motion.div
                key={currentStep}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={transition}
                className="w-full"
            >
                {childrenArray[currentStep]}
            </motion.div>
        </AnimatePresence>
    );
};

// StepperProgress component
export const StepperProgress = (props) => {
    const { steps, currentStep, goToStep } = useStepper();
    const lineRefs = useRef(steps.map(() => useAnimation()));
    const [prevStep, setPrevStep] = useState(currentStep);
    const [hasInitialized, setHasInitialized] = useState(false);

    useEffect(() => {
        // Initial setup of lines
        if (!hasInitialized) {
            steps.forEach((_, index) => {
                if (index < currentStep) {
                    lineRefs.current[index].set({ scaleX: 1 });
                } else {
                    lineRefs.current[index].set({ scaleX: 0 });
                }
            });
            setHasInitialized(true);
            return;
        }

        // Animate lines when step changes
        const animateLines = async () => {
            const isMovingForward = currentStep > prevStep;
            const start = Math.min(prevStep, currentStep);
            const end = Math.max(prevStep, currentStep);
            const stepsToAnimate = Math.abs(currentStep - prevStep);
            
            // Total animation duration (in seconds)
            const totalDuration = 0.3;
            // Duration per step
            const stepDuration = totalDuration / stepsToAnimate;

            if (isMovingForward) {
                for (let i = start; i < end; i++) {
                    await lineRefs.current[i].start({ scaleX: 1 }, { duration: stepDuration });
                }
            } else {
                for (let i = end - 1; i >= start; i--) {
                    await lineRefs.current[i].start({ scaleX: 0 }, { duration: stepDuration });
                }
            }
        };

        animateLines();
        setPrevStep(currentStep);
    }, [currentStep, steps.length, prevStep, hasInitialized]);

    return (
        <nav aria-label="Progress" {...props}>
            <ol role="list" className="flex items-center justify-between w-full max-w-4xl mx-auto">
                {steps.map((step, stepIdx) => {
                    const isClickable = !step.disabled && (stepIdx <= currentStep || steps.slice(0, stepIdx).every(step => step.isComplete));
                    const isCompleted = stepIdx < currentStep || step.isComplete;

                    return (
                        <li 
                            key={step.name} 
                            className={cn(
                                "relative flex-1 flex flex-col items-center",
                                step.disabled ? "cursor-not-allowed" :
                                isClickable ? "group cursor-pointer" : "cursor-not-allowed"
                            )}
                            onClick={() => !step.disabled && isClickable && goToStep(stepIdx)}
                        >
                            {/* Step Text */}
                            <span className={cn(
                                'mb-4 font-normal whitespace-nowrap text-xs md:text-base',
                                step.disabled ? 'text-muted-foreground/50' :
                                stepIdx === currentStep ? 'text-foreground' : 
                                isClickable ? 'text-muted-foreground group-hover:text-foreground' : 'text-muted-foreground/50',
                                'transition-colors'
                            )}>
                                {step.name}
                            </span>

                            {/* Animated Line */}
                            {stepIdx !== steps.length - 1 && (
                                <div className="absolute bottom-[0.45rem] left-1/2 w-full h-0.5 bg-muted">
                                    <motion.div
                                        className={cn('h-full bg-violet-500 origin-left',
                                            step.disabled ? 'bg-[#4F3C7C]' : ''
                                        )}
                                        initial={{ scaleX: isCompleted ? 1 : 0 }}
                                        animate={lineRefs.current[stepIdx]}
                                        transition={{ duration: 0.3 }}
                                    />
                                </div>
                            )}

                            {/* Stepper Circle */}
                            <motion.div
                                className={cn(
                                    'relative flex items-center justify-center w-4 h-4 rounded-full border-2 transition-colors',
                                    isClickable && 'group-focus:outline-none group-focus-visible:ring-2 group-focus-visible:ring-violet-500 group-focus-visible:ring-opacity-50',
                                    step.disabled ? 'bg-[#4F3C7C] border-[#4F3C7C]' :
                                    isCompleted ? 'bg-violet-500 border-violet-500' :
                                    stepIdx === currentStep ? 'bg-background border-violet-500' :
                                    'bg-background border-muted'
                                )}
                                animate={{
                                    scale: stepIdx === currentStep ? [1, 1.1, 1] : 1,
                                }}
                                transition={{ duration: 0.3 }}
                            >
                                <span className="sr-only">{step.name}</span>
                            </motion.div>
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
};